import { normalizeUploadContentType } from '@sitewise/shared';

import { api } from '../lib/api-client.js';

import type { PresignUploadInput, PresignUploadResult } from '@sitewise/shared';

/**
 * Coerces a filename to the character set the presign schema accepts
 * (`[\w.\- ()]`). Real-world files carry commas, ampersands, accented
 * letters, and emoji — all of which would otherwise 400 at presign. The
 * server namespaces the stored object under a UUID, so the filename is only
 * a human-readable suffix and is safe to rewrite.
 */
export function sanitizeUploadFilename(filename: string): string {
  const cleaned = filename
    .replace(/[^\w.\- ()]+/g, '_')
    .replace(/_{2,}/g, '_')
    .trim()
    .slice(0, 200);
  return cleaned.length > 0 ? cleaned : 'upload';
}

export async function presignUpload(input: PresignUploadInput): Promise<PresignUploadResult> {
  return api<PresignUploadResult>('/uploads/presign', {
    method: 'POST',
    body: {
      ...input,
      contentType: normalizeUploadContentType(input.contentType),
      filename: sanitizeUploadFilename(input.filename),
    },
  });
}

/**
 * Convenience helper: presigns + PUTs the file body to the returned
 * URL. Returns the upload row's id so the caller can reference it
 * (e.g. to kick off a CSV import job).
 */
export async function uploadFile(file: File, kind: PresignUploadInput['kind']): Promise<{ uploadId: string; objectKey: string }> {
  const presign = await presignUpload({
    kind,
    contentType: file.type || 'application/octet-stream',
    filename: file.name,
    sizeBytes: file.size,
  });

  const res = await fetch(presign.uploadUrl, {
    method: 'PUT',
    headers: presign.headers,
    body: file,
  });
  if (!res.ok) {
    throw new Error(`Upload failed: HTTP ${res.status}`);
  }
  return { uploadId: presign.uploadId, objectKey: presign.objectKey };
}

export async function fetchUploadSignedUrl(
  uploadId: string,
): Promise<{ url: string; expiresAt: string }> {
  return api(`/uploads/${uploadId}/signed-url`);
}
